/**
 * Displays a single extracted home loan.
 *
 * Single-click view (always visible):
 *   - Bank · loan type · ₹amount · status pill
 *   - Headline strip: ₹amount · rate% type · ₹EMI · active till YYYY
 *   - "No prepayment or penalty" / "Reset every N" / "Co-applicants: Yes"
 *
 * Detailed (Show more) — 6 sections:
 *   1. Loan structure         sanction date · benchmark · spread · effective · reset
 *   2. EMI & tenure           EMI · remaining tenure · outstanding principal
 *   3. Charges & fees         processing · admin · penal · conversion charges
 *   4. Insurance & documents  insurance requirement + compliance notes
 *   5. Things to know         standard regulatory / RBI disclosures
 *   6. Emergency / closure    branch + customer care + grievance email + RM
 */
import * as React from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from '@finmin/ui';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  HomeIcon,
  Info,
  Landmark,
  Mail,
  Phone,
  ReceiptText,
  ShieldAlert,
  Users,
  XCircle,
} from 'lucide-react';
import type { HomeLoanExtracted } from '@/api/profile-documents';
import {
  formatBenchmark,
  formatDate,
  formatINR,
  formatINRCompact,
  formatInterestType,
  formatLoanStatus,
  formatPct,
  monthsToYears,
} from '@/features/insurance/format';

interface Props {
  loan: HomeLoanExtracted;
  defaultExpanded?: boolean;
}

export function HomeLoanCard({ loan: l, defaultExpanded = false }: Props) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const status = formatLoanStatus(l.loan_status);

  const subtitle = [
    l.loan_account_number ? `A/c ${l.loan_account_number}` : null,
    l.branch_name,
  ]
    .filter(Boolean)
    .join(' · ');

  const interestType = formatInterestType(l.interest_rate_type);
  const headlineRate =
    l.current_interest_rate_pct != null
      ? `${formatPct(l.current_interest_rate_pct)} ${interestType !== '—' ? interestType : ''}`.trim()
      : null;

  const noChargesNote =
    isNilLike(l.part_prepayment_charges_text) && isNilLike(l.foreclosure_charges_text);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <HomeIcon className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
              <span className="truncate">
                {l.bank_name ?? 'Home loan'}
                {l.loan_type === 'HOME_LOAN' ? ' Home Loan' : ''}
              </span>
            </CardTitle>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
            )}
          </div>
          {l.is_manually_edited && (
            <Badge variant="outline" className="shrink-0 text-xs">
              Edited
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* ── Single-click headline ─────────────────────────────────────── */}
        <section className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
          <Stat
            label="Loan amount"
            value={formatINRCompact(l.sanctioned_amount)}
            sub={l.outstanding_principal ? `O/s ${formatINRCompact(l.outstanding_principal)}` : undefined}
          />
          <Stat
            label="Rate"
            value={headlineRate ?? '—'}
            sub={l.reset_frequency ? `reset ${l.reset_frequency.toLowerCase()}` : undefined}
          />
          <Stat
            label="EMI"
            value={formatINRCompact(l.monthly_emi)}
            sub="monthly"
          />
          <Stat
            label="Status"
            value={<StatusPill tone={status.tone}>{status.label}</StatusPill>}
            sub={l.maturity_date ? `till ${formatDate(l.maturity_date)}` : undefined}
          />
        </section>

        {/* ── Quick callouts ────────────────────────────────────────────── */}
        <section className="flex flex-wrap items-center gap-2 text-xs">
          {noChargesNote && (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3 text-success" aria-hidden />
              No prepayment or foreclosure penalty
            </Badge>
          )}
          {l.has_co_applicants && (
            <Badge variant="secondary" className="gap-1">
              <Users className="h-3 w-3" aria-hidden />
              Co-applicants: {l.co_applicants.length || 'Yes'}
            </Badge>
          )}
          {l.tenure_months && (
            <Badge variant="outline" className="gap-1">
              <Clock className="h-3 w-3" aria-hidden />
              {monthsToYears(l.tenure_months)} term
            </Badge>
          )}
        </section>

        {/* ── Show-more details ─────────────────────────────────────────── */}
        {expanded && (
          <div className="space-y-5 border-t border-border pt-5">
            {/* Section 1 — Loan structure */}
            <section>
              <Label>
                <Landmark className="h-3 w-3 inline mr-1" aria-hidden />
                Loan structure
              </Label>
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                <Mini label="Sanction date" value={formatDate(l.sanction_date)} />
                <Mini label="First EMI" value={formatDate(l.first_emi_date)} />
                <Mini label="Maturity" value={formatDate(l.maturity_date)} />
                <Mini label="Benchmark" value={formatBenchmark(l.benchmark)} />
                <Mini
                  label="Benchmark rate"
                  value={l.benchmark_rate_pct != null ? formatPct(l.benchmark_rate_pct) : '—'}
                />
                <Mini
                  label="Spread"
                  value={l.spread_pct != null ? formatPct(l.spread_pct) : '—'}
                />
                <Mini
                  label="Effective rate"
                  value={
                    l.current_interest_rate_pct != null
                      ? formatPct(l.current_interest_rate_pct)
                      : '—'
                  }
                />
                <Mini
                  label="Reset"
                  value={l.reset_frequency ?? '—'}
                />
                <Mini
                  label="Rate type"
                  value={formatInterestType(l.interest_rate_type)}
                />
              </dl>
            </section>

            {/* Section 2 — EMI & tenure */}
            <section>
              <Label>EMI & tenure</Label>
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                <Mini label="EMI" value={formatINRCompact(l.monthly_emi)} />
                <Mini
                  label="Tenure"
                  value={monthsToYears(l.tenure_months)}
                />
                <Mini
                  label="Remaining"
                  value={monthsToYears(l.remaining_tenure_months)}
                />
                <Mini
                  label="Outstanding"
                  value={formatINRCompact(l.outstanding_principal)}
                />
              </dl>
              {l.interest_rate_type === 'FLOATING' && (
                <div className="mt-3 rounded-md border border-warning/30 bg-warning/5 p-3 flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" aria-hidden />
                  <p className="text-xs text-muted-foreground">
                    <span className="text-foreground font-medium">
                      Floating rate
                    </span>{' '}
                    — if rates rise, the bank typically extends your tenure (not your
                    EMI) up to a ceiling, then revises the EMI. Watch the next reset
                    cycle in {l.reset_frequency ?? '—'}.
                  </p>
                </div>
              )}
            </section>

            {/* Section 3 — Charges & fees */}
            <section>
              <Label>
                <ReceiptText className="h-3 w-3 inline mr-1" aria-hidden />
                Charges & fees
              </Label>
              <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
                <Mini label="Processing fee" value={l.processing_fee_text ?? '—'} />
                <Mini label="Admin fee" value={l.administrative_fee_text ?? '—'} />
                <Mini label="Penal interest" value={l.penal_interest_text ?? '—'} />
                <Mini
                  label="Conversion (float→fix)"
                  value={l.conversion_charges_text ?? '—'}
                />
                <Mini
                  label="Part-prepayment"
                  value={l.part_prepayment_charges_text ?? '—'}
                />
                <Mini label="Foreclosure" value={l.foreclosure_charges_text ?? '—'} />
              </dl>
            </section>

            {/* Section 4 — Insurance & co-applicants */}
            {(l.insurance_required ||
              l.insurance_provider ||
              l.co_applicants.length > 0) && (
              <section>
                <Label>Insurance & co-applicants</Label>
                <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
                  <Mini
                    label="Insurance"
                    value={
                      l.insurance_required === true
                        ? l.insurance_provider
                          ? `Required · ${l.insurance_provider}`
                          : 'Required'
                        : l.insurance_required === false
                          ? 'Not required'
                          : '—'
                    }
                  />
                  <Mini
                    label="Co-applicants"
                    value={
                      l.co_applicants.length > 0
                        ? `${l.co_applicants.length}`
                        : l.has_co_applicants
                          ? 'Yes (details not extracted)'
                          : '—'
                    }
                  />
                </dl>
                {l.co_applicants.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm">
                    {l.co_applicants.map((c, i) => (
                      <li key={i} className="text-foreground">
                        <span className="font-medium">{c.name ?? '—'}</span>
                        {c.relationship && (
                          <span className="text-muted-foreground"> · {c.relationship}</span>
                        )}
                        {c.pan && (
                          <span className="text-muted-foreground font-mono"> · {c.pan}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {/* Section 5 — Things to know (standard RBI / consumer disclosures) */}
            <RegulatorySummary loan={l} />

            {/* Section 6 — Emergency / closure */}
            {(l.branch_address ||
              l.branch_phone ||
              l.branch_manager_name ||
              l.customer_care_phone ||
              l.grievance_email) && (
              <section className="rounded-md border-2 border-destructive/20 bg-destructive/5 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-destructive" aria-hidden />
                  <p className="text-sm font-semibold text-foreground">
                    Emergency / closure contacts
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Branch details, customer care, and grievance routes — at
                  closure these are where you reclaim property documents.
                </p>

                <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 mt-2">
                  {l.branch_manager_name && (
                    <Mini label="Branch manager" value={l.branch_manager_name} />
                  )}
                  {l.branch_address && (
                    <Mini label="Branch address" value={l.branch_address} />
                  )}
                </dl>

                <div className="space-y-1.5 pt-1">
                  {l.branch_phone && (
                    <ContactLine
                      icon={<Building2 className="h-3.5 w-3.5 text-muted-foreground" />}
                      href={`tel:${l.branch_phone}`}
                      value={l.branch_phone}
                      meta="Branch"
                    />
                  )}
                  {l.customer_care_phone && (
                    <ContactLine
                      icon={<Phone className="h-3.5 w-3.5 text-destructive" />}
                      href={`tel:${l.customer_care_phone}`}
                      value={l.customer_care_phone}
                      meta="Customer care"
                    />
                  )}
                  {l.grievance_email && (
                    <ContactLine
                      icon={<Mail className="h-3.5 w-3.5 text-muted-foreground" />}
                      href={`mailto:${l.grievance_email}`}
                      value={l.grievance_email}
                      meta="Grievance"
                    />
                  )}
                </div>
              </section>
            )}

            {l.extraction_notes && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
                <p>{l.extraction_notes}</p>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Sanctioned: <span className="font-medium">{formatINR(l.sanctioned_amount)}</span>{' '}
              · Extracted {formatDate(l.extracted_at)}
            </p>
          </div>
        )}

        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            className="gap-1.5 text-xs"
          >
            {expanded ? (
              <>
                Show less
                <ChevronUp className="h-3 w-3" aria-hidden />
              </>
            ) : (
              <>
                View loan details
                <ChevronDown className="h-3 w-3" aria-hidden />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      <div className="text-sm font-semibold text-foreground mt-0.5 tabular-nums">
        {value}
      </div>
      {sub && (
        <p className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</p>
      )}
    </div>
  );
}

function StatusPill({
  tone,
  children,
}: {
  tone: 'success' | 'warning' | 'destructive' | 'muted';
  children: React.ReactNode;
}) {
  const Icon =
    tone === 'success' ? CheckCircle2 : tone === 'destructive' ? XCircle : Clock;
  const toneClass: Record<typeof tone, string> = {
    success: 'bg-success/10 text-success border-success/20',
    warning: 'bg-warning/10 text-warning border-warning/30',
    destructive: 'bg-destructive/10 text-destructive border-destructive/20',
    muted: 'bg-secondary text-muted-foreground border-border',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        toneClass[tone],
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {children}
    </span>
  );
}

function Mini({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
      {children}
    </span>
  );
}

function ContactLine({
  icon,
  href,
  value,
  meta,
}: {
  icon: React.ReactNode;
  href: string;
  value: string;
  meta?: string;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon}
      <a href={href} className="font-medium hover:underline">
        {value}
      </a>
      {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
    </div>
  );
}

function RegulatorySummary({ loan }: { loan: HomeLoanExtracted }) {
  return (
    <section className="rounded-md bg-secondary/40 p-3 space-y-2">
      <div className="flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 text-warning" aria-hidden />
        <Label>Things to know</Label>
      </div>
      <ul className="space-y-1.5 text-xs text-muted-foreground list-disc pl-5">
        <li>
          <span className="text-foreground">No prepayment penalty</span> on
          floating-rate home loans to individuals (RBI rule). Lump-sum payments
          go straight against principal and cut years off the term.
        </li>
        <li>
          <span className="text-foreground">Tenure-extension on rate hikes</span> —
          banks typically stretch tenure first, raise EMI only when they hit the
          ceiling. Check your reset cycle and re-fix the EMI deliberately.
        </li>
        <li>
          <span className="text-foreground">Property documents</span> stay with
          the bank until full closure; on closure, demand an NOC and the
          original documents within the bank's stated SLA.
        </li>
        <li>
          <span className="text-foreground">PAN-Aadhaar linkage</span> — keep
          these linked or the bank may apply a higher TDS rate on interest
          certificates and 80C / 24(b) deductions can be questioned.
        </li>
        {loan.interest_rate_type === 'FLOATING' && (
          <li className="text-foreground/70">
            Your loan is repo-linked — every RBI repo change passes through at
            the next reset.
          </li>
        )}
      </ul>
    </section>
  );
}

function isNilLike(s: string | null | undefined): boolean {
  if (!s) return false;
  const v = s.trim().toLowerCase();
  return v === 'nil' || v === 'zero' || v === '0' || v === '0%' || v === 'none';
}
